import { useCallback } from 'react'
import { useShallowSelector } from '../../../Workspace2Store'
import type { DataNode } from '../../../types'
import { findRootArticle } from '../../../utils'

/**
 * Hook that handles collapsing parent articles when creating new content.
 * This helps maintain a cleaner view and better focus on newly created content.
 *
 * @returns {Object} An object containing the collapseParentArticle function
 */
export const useArticleCollapse = () => {
    const {
        contentItemViewNodes,
        expandedKeys,
        setExpandedKeys,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        expandedKeys: state.expandedKeys,
        setExpandedKeys: state.actions.setExpandedKeys,
    }))

    const collapseParentArticle = useCallback(
        (sibling: DataNode) => {
            // find the parent article ID - prioritize rootArticleId if available
            const parentArticleId =
                sibling.rootArticleId ||
                findRootArticle(sibling.key, contentItemViewNodes)

            if (!parentArticleId) {
                return
            }

            // only collapse if the parent article is currently expanded
            if (expandedKeys.includes(parentArticleId)) {
                const newExpandedKeys = expandedKeys.filter(
                    (key) => key !== parentArticleId
                )
                setExpandedKeys(newExpandedKeys)
            }
        },
        [contentItemViewNodes, expandedKeys, setExpandedKeys]
    )

    return {
        collapseParentArticle,
    }
}
